import { OfferVariant } from '@lambda/commons/apis/elasticsearch/types';
import { REEBELO_US_SERVICE_PROVIDERS } from '@lambda/reebelo';
import { isEmpty } from 'lodash-es';
import {
  Conditions,
  ReplacedProduct,
} from '@/lib/collections/catalogue/helpers';
import {
  CleanOffersVariantsT,
  DynamicOfferTreeT,
  ProductVariant,
} from './types';
import settings from '@/settings';
import { logger } from '@/lib/logger';

export const generateColors = (
  colors: Record<string, string>,
  colorsInHex: Record<string, string>,
) =>
  Object.fromEntries(
    Object.entries(colors).map(([key, value]) => [value, colorsInHex[key]]),
  );

export const generateSelection = (
  rVariants: OfferVariant[] | undefined = [],
) => {
  const selection: Record<string, string> = {};

  rVariants.forEach((v) => {
    selection[v.handle] = v.value;
  });

  return selection;
};

const toCamelCase = (str: string): string =>
  str
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, char) => char.toUpperCase());

export const cleanOffersVariants = ({
  offers,
  rVariants,
  psku,
  category,
}: CleanOffersVariantsT) => {
  const cleanOffers = offers
    .map((offer) => {
      if (offer.rVariants) {
        const additionalVariants = [];

        // Add service provider variant if it exists
        if (offer?.serviceProvider) {
          additionalVariants.push({
            handle: 'serviceProvider',
            name: 'Carrier',
            value: offer.serviceProvider,
          });
        }

        // Add condition variant if it exists
        if (offer?.condition) {
          additionalVariants.push({
            handle: 'condition',
            name: 'Condition',
            value: offer.condition,
          });
        }

        return {
          ...offer,
          rVariants: [...offer.rVariants, ...additionalVariants].filter((v) => {
            const blackListed = [
              'cellularStatus',
              'cellularNetwork',
              'networkProvider',
            ];

            return !blackListed.includes(v.handle);
          }),
        };
      }

      return offer;
    })
    .filter((offer) => {
      if (offer.rVariants) {
        offer.rVariants.every((v) => {
          const isPossibleOption = rVariants
            ?.find((pv) => pv.handle === v.handle)
            ?.values?.includes(v.value);

          if (!isPossibleOption && !offer.store.includes('#disabled')) {
            // We only want to log the error if the offer is in stock
            const generateProductVariantsError = () => {
              if (rVariants.length === 0) return 'Missing product rVariants';

              const productVariant = rVariants.find(
                (rv) => rv.handle === v.handle,
              );

              if (productVariant == null)
                return `Missing variant within product rVariants`;

              return productVariant;
            };

            logger.warn(
              {
                category,
                psku,
                sku: offer?.sku ?? '',
                variantHandle: v.handle,
                variantValue: v.value,
                productVariants: generateProductVariantsError(),
                stock: offer?.stock ?? 0,
                productId: offer?.productId ?? '',
              },
              'Impossible rVariant Option',
            );
          }

          return v.value && isPossibleOption;
        });
      }

      return (
        offer.rVariants &&
        offer.rVariants.length === rVariants.length &&
        // Ensure at least one rVariant option matches the provided rVariants options
        offer.rVariants.every((v) => {
          const isPossibleOption = rVariants
            ?.find((pv) => pv.handle === v.handle)
            ?.values?.includes(v.value);

          return v.value && isPossibleOption;
        })
      );
    });

  return cleanOffers;
};

export const generateVariants = (
  offers: ReplacedProduct[],
  _rVariants: { variant: string; values: string[] | Record<string, string> }[],
  _rVariantsOrder: string[] | undefined,
  _rVariantsValuesOrder: Record<string, Array<string>> | undefined,
): ProductVariant[] => {
  if (_rVariants.length === 0) return [];

  const blackListed = ['cellularStatus', 'cellularNetwork', 'networkProvider'];

  // Handle Sorting
  const streamlinedVariants = _rVariantsOrder
    ? [..._rVariants]
        .sort((a, b) => {
          const indexA = _rVariantsOrder.indexOf(toCamelCase(a.variant));
          const indexB = _rVariantsOrder.indexOf(toCamelCase(b.variant));

          return (
            (indexA === -1 ? Infinity : indexA) -
            (indexB === -1 ? Infinity : indexB)
          );
        })
        .map((variant) => ({
          ...variant,
          values:
            _rVariantsValuesOrder?.[variant.variant] &&
            Array.isArray(variant.values)
              ? [...variant.values].sort((a, b) => {
                  const valueOrder = _rVariantsValuesOrder[variant.variant];

                  return (
                    (valueOrder.indexOf(a) === -1
                      ? Infinity
                      : valueOrder.indexOf(a)) -
                    (valueOrder.indexOf(b) === -1
                      ? Infinity
                      : valueOrder.indexOf(b))
                  );
                })
              : variant.values,
        }))
    : _rVariants;

  const rVariants: ProductVariant[] = streamlinedVariants
    .filter(
      (v) =>
        !blackListed.includes(toCamelCase(v.variant)) && !isEmpty(v.values),
    )
    .map((v) => {
      const camelCaseVariant = toCamelCase(v.variant);

      return Array.isArray(v.values)
        ? { name: v.variant, handle: camelCaseVariant, values: v.values }
        : {
            name: v.variant,
            handle: camelCaseVariant,
            values: Object.values(v.values as Record<string, string>).sort(),
          };
    });

  const conditionVariant: ProductVariant = {
    handle: 'condition',
    name: 'Condition',
    values: Object.values(Conditions.en)
      .reverse()
      .filter((sp) => offers.some((offer) => offer.condition === sp)),
  };

  const batteryHealthIndex = rVariants.findIndex(
    (v) => v.handle === 'batteryHealth',
  );
  const batteryHealthVariant =
    batteryHealthIndex !== -1
      ? rVariants.splice(batteryHealthIndex, 1)[0]
      : null;

  rVariants.push(conditionVariant);
  if (batteryHealthVariant) rVariants.push(batteryHealthVariant);

  const serviceProviderVariant: ProductVariant = {
    handle: 'serviceProvider',
    name: 'Carrier',
    values: REEBELO_US_SERVICE_PROVIDERS.filter((sp) =>
      offers.some((offer) => offer.serviceProvider === sp),
    ),
  };

  if (settings.store === 'reebelo-us' && serviceProviderVariant.values.length)
    rVariants.unshift(serviceProviderVariant);

  return rVariants;
};

export class DynamicOfferTree {
  tree: DynamicOfferTreeT;

  orderedPath: string[];

  private readonly productVariants: string[];

  constructor(offers: ReplacedProduct[], rVariants: ProductVariant[]) {
    this.productVariants = rVariants.map((v) => v.handle);

    const pathToOffer: DynamicOfferTreeT = {};

    offers.forEach((offer) => {
      const selection = generateSelection(offer.rVariants);
      const path = this.toPath(selection);

      pathToOffer[path] = offer;
    });

    this.tree = pathToOffer;
    this.orderedPath = Object.keys(pathToOffer).sort(
      (pathA, pathB) => pathToOffer[pathA].price - pathToOffer[pathB].price,
    );
  }

  get(
    selection: Record<string, string>,
    nextSelection?: Record<string, string>,
  ) {
    // ✅ If no nextSelection match, check selection
    const path = this.toPath({ ...selection, ...nextSelection });
    const directGet = this.tree[path];

    if (directGet)
      return { offer: directGet, suggested: false, shouldHide: false };

    // ✅ Build suggestedPath by prioritizing nextSelection over selection
    const suggestedPath = this.productVariants.map(
      (variant) => nextSelection?.[variant] || selection[variant] || '',
    );

    let bestOffer: ReplacedProduct | null = null;
    let bestMatchCount = -1;

    // ✅ Iterate through orderedPath in reverse to find the best match
    this.orderedPath.forEach((pathKey) => {
      const offer = this.tree[pathKey];

      if (!offer) return;

      const offerSelection = pathKey.split(',');

      // ✅ Check if any values in nextSelection are missing in the offerSelection
      const missingValues = this.productVariants.some(
        (variant, index) =>
          nextSelection?.[variant] &&
          offerSelection[index] !== nextSelection[variant],
      );

      // If any nextSelection value is missing or different in the offer, skip it
      if (missingValues) return;

      // Count matches between offerSelection and suggestedPath
      let matchCount = 0;

      offerSelection.forEach((part, index) => {
        if (part === suggestedPath[index] && part !== '') matchCount += 1;
      });

      // ✅ Track the best match with the highest number of matches
      if (matchCount > bestMatchCount) {
        bestOffer = offer;
        bestMatchCount = matchCount;
      }
    });

    // ✅ Return the best match or fallback to the first item in orderedPath
    return {
      offer: bestOffer || this.tree[this.orderedPath[0]],
      suggested: true,
      shouldHide: !bestOffer,
    };
  }

  // Converts the selection object to a path
  toPath = (selection: Record<string, string>) => {
    const path = this.productVariants.map((pv) => selection[pv]).join(',');

    return path;
  };
}
